// src/components/HomeSearchSuggestions.ts

type HomeSuggestionSelectHandler = (value: string) => void;

const MAX_SUGGESTIONS = 5;

/**
 * Render the HOME-PAGE search suggestion dropdown.
 *
 * This component intentionally owns its DOM and uses the opaque
 * .home-suggestions-* styles from Home.css. It does not reuse the SERP
 * SearchSuggestions component.
 */
export function renderHomeSearchSuggestions(
  query: string,
  onSelect: HomeSuggestionSelectHandler,
  container: HTMLElement,
): void {
  const cleanQuery = query.trim();

  if (!cleanQuery) {
    container.innerHTML = "";
    return;
  }

  const suggestions = buildSuggestions(cleanQuery).slice(0, MAX_SUGGESTIONS);

  container.innerHTML = `
    <div
      class="home-suggestions-container"
      role="listbox"
      aria-label="Search suggestions"
    >
      ${suggestions
        .map(
          (suggestion) => `
            <button
              class="home-suggestion-item"
              type="button"
              role="option"
              data-value="${escapeAttribute(suggestion)}"
            >
              <span class="home-suggestion-icon" aria-hidden="true">🔍</span>
              <span class="home-suggestion-label">${escapeHtml(suggestion)}</span>
            </button>
          `,
        )
        .join("")}
    </div>
  `;

  container
    .querySelectorAll<HTMLButtonElement>(".home-suggestion-item")
    .forEach((item) => {
      item.addEventListener("click", () => {
        const value = item.dataset.value;
        if (!value) return;

        container.innerHTML = "";
        onSelect(value);
      });
    });
}

function buildSuggestions(query: string): string[] {
  return [
    `${query} news`,
    `${query} images`,
    `${query} videos`,
    `${query} price`,
    `${query} wiki`,
  ];
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function escapeAttribute(value: string): string {
  return escapeHtml(value);
}
